import { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import cn from "classnames";
import {
  HomeIcon, FolderIcon,
  ArrowRightOnRectangleIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  SparklesIcon,
  CircleStackIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import useAuthStore from "../../store/authStore";
import useCopilotStore from "../../store/copilotStore";
import { LLM_ENABLED } from "../../utils/flags";
import useSettingsStore from "../../store/settingsStore";
import { buildTheme, THEMES } from "../../themes";
import UserSettingsModal from "../ui/UserSettingsModal";

const navItems = [
  { to: "/dashboard", icon: HomeIcon,       label: "Dashboard" },
  { to: "/projects",  icon: FolderIcon,     label: "Projects"  },
  { to: "/designs",   icon: BookOpenIcon,   label: "Designs"   },
];

const DOCS_SECTIONS = [
  { id: "intro",     label: "Introduction" },
  { id: "workflow",  label: "Typical Workflow" },
  { id: "projects",  label: "Projects & Designs" },
  { id: "layout",    label: "Layout Tool" },
  { id: "editor",    label: "Design Editor" },
  { id: "summary",   label: "Summary Tab" },
  { id: "hydraulic", label: "Hydraulic Layout" },
  { id: "meco",      label: "Meridional Contour" },
  { id: "edges",     label: "Edges" },
  { id: "beta",      label: "Beta Angles" },
  { id: "thickness", label: "Thickness" },
  { id: "transform", label: "Transform" },
  { id: "views",     label: "2D & 3D Views" },
  { id: "curvature", label: "Curvature" },
  { id: "cutoff",    label: "Cutoff" },
  { id: "export",    label: "Export" },
  { id: "assembly",  label: "Assembly" },
  { id: "copilot",   label: "Pump Copilot" },
  { id: "library",   label: "Component Library" },
  { id: "shortcuts", label: "Keyboard Shortcuts" },
  { id: "admin",     label: "Admin Panel" },
];

export default function Sidebar() {
  const { user, logout } = useAuthStore();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const theme = user?.entity_theme ? buildTheme(user.entity_theme) : (THEMES[user?.entity] ?? THEMES.fastblade);
  const { isOpen: copilotOpen, togglePanel } = useCopilotStore();
  const copilotEnabled = useSettingsStore((s) => s.copilotEnabled);
  const navigate = useNavigate();
  const location = useLocation();

  const docsButtonRef = useRef(null);
  const docsFlyoutRef = useRef(null);

  useEffect(() => {
    if (!docsOpen) return;
    const handleClick = (e) => {
      if (
        docsFlyoutRef.current && !docsFlyoutRef.current.contains(e.target) &&
        docsButtonRef.current && !docsButtonRef.current.contains(e.target)
      ) {
        setDocsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [docsOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside className="flex flex-col w-60 bg-gray-900 text-white min-h-screen shrink-0">
      {/* Logo */}
      <div className="mx-3 mt-4 mb-2 rounded-xl bg-gray-800 px-4 py-3 border border-gray-700 text-center">
        <div className="flex items-center justify-center gap-2">
          {theme.headerLogo && (
            <img src={theme.headerLogo.src} alt={theme.headerLogo.alt}
                 className={theme.headerLogo.className} />
          )}
          <p className="text-xl font-bold tracking-tight text-bh-400">{theme.appName}</p>
        </div>
        {theme.poweredBy && (
          <div className="flex items-center justify-center gap-1.5 mt-1.5">
            <span className="text-xs text-gray-500">{theme.poweredBy.label}</span>
            <a href={theme.poweredBy.href} target="_blank" rel="noopener noreferrer"
               className="hover:opacity-70 transition-opacity" title={theme.poweredBy.alt}>
              <img src={theme.poweredBy.logo} alt={theme.poweredBy.alt}
                   className="h-4 w-auto"
                   style={{ filter: "brightness(0) invert(1)", opacity: 0.75 }} />
            </a>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn("flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive
                  ? "bg-bh-600 text-white"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white")
            }
          >
            <Icon className="h-5 w-5 shrink-0" />
            {label}
          </NavLink>
        ))}

        {/* Copilot toggle — hidden when LLM disabled or copilot turned off */}
        {LLM_ENABLED && copilotEnabled && (
          <>
            <div className="border-t border-gray-700 my-2" />
            <button
              onClick={togglePanel}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium w-full transition-colors",
                copilotOpen
                  ? "bg-bh-600 text-white"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white"
              )}
            >
              <SparklesIcon className="h-5 w-5 shrink-0" />
              Pump Copilot
            </button>
          </>
        )}

        {/* ── Documentation ── */}
        <div className="border-t border-gray-700 my-2" />
        <button
          ref={docsButtonRef}
          onClick={() => setDocsOpen((o) => !o)}
          className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium w-full transition-colors",
            docsOpen || location.pathname === "/docs"
              ? "bg-bh-600 text-white"
              : "text-gray-300 hover:bg-gray-800 hover:text-white"
          )}
        >
          <DocumentTextIcon className="h-5 w-5 shrink-0" />
          Documentation
        </button>

        {/* Docs flyout — fixed panel to the right of the sidebar */}
        {docsOpen && (
          <div
            ref={docsFlyoutRef}
            className="fixed top-0 left-60 h-screen w-52 bg-gray-900 border-r border-gray-700 z-50 flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-700 shrink-0">
              <span className="text-sm font-semibold text-white">Documentation</span>
              <button
                onClick={() => { navigate("/docs"); setDocsOpen(false); }}
                className="text-xs text-bh-400 hover:text-bh-300 transition-colors font-medium"
              >
                Open full →
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-2 px-2">
              {DOCS_SECTIONS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => { navigate(`/docs#${id}`); setDocsOpen(false); }}
                  className="w-full text-left px-3 py-1.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-gray-800 transition-colors"
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {user?.is_admin && (
          <>
            {/* ── Admin ── */}
            <div className="border-t border-gray-700 my-2" />
            <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500">Admin</p>
            {[
              { to: "/admin/entities",    label: "Entities",     icon: Cog6ToothIcon },
              { to: "/admin/users",       label: "Users",        icon: Cog6ToothIcon },
              { to: "/admin/lookups",     label: "Lookup Types", icon: Cog6ToothIcon },
              { to: "/admin/cheers",      label: "Cheers",       icon: Cog6ToothIcon },
              { to: "/admin/stat-curves", label: "Stat Curves",  icon: ChartBarIcon  },
            ].map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn("flex items-center gap-3 pl-6 pr-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-bh-600 text-white"
                      : "text-gray-300 hover:bg-gray-800 hover:text-white")
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </NavLink>
            ))}

            {/* ── LLM — hidden when feature flag is off ── */}
            {LLM_ENABLED && (<>
              <div className="border-t border-gray-700 my-2" />
              <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
                <SparklesIcon className="h-3 w-3" />
                LLM
              </p>
              {[
                { to: "/admin/settings",       label: "Settings",       icon: AdjustmentsHorizontalIcon },
                { to: "/admin/knowledge-base", label: "Knowledge Base", icon: CircleStackIcon },
              ].map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    cn("flex items-center gap-3 pl-6 pr-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "bg-bh-600 text-white"
                        : "text-gray-300 hover:bg-gray-800 hover:text-white")
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                </NavLink>
              ))}
            </>)}
          </>
        )}
      </nav>

      {/* User */}
      <div className="px-3 py-4 border-t border-gray-700">
        <button
          onClick={() => setSettingsOpen(true)}
          className="flex items-center gap-3 w-full px-2 py-2 rounded-lg hover:bg-gray-800 transition-colors group mb-1"
          title="Account settings"
        >
          <div className="w-8 h-8 rounded-full bg-bh-500 flex items-center justify-center text-sm font-semibold shrink-0 group-hover:ring-2 group-hover:ring-bh-400 transition-all">
            {user?.full_name?.[0] || user?.username?.[0] || "U"}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-medium truncate">{user?.full_name || user?.username}</p>
            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
          </div>
        </button>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
        >
          <ArrowRightOnRectangleIcon className="h-4 w-4" />
          Sign out
        </button>
      </div>

      <UserSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </aside>
  );
}
